import apiClientV2 from '@/api/clientV2';
import type {
  AuthorizeResponse,
  CallbackResponse,
  ContaAzulIntegration,
  CostCenter,
  FinancialAccount,
  Category,
  Supplier,
  CreateSupplierInput,
  ContaAzulEntryData,
  ContaAzulEntryDataInput,
  SyncJobResponse,
} from './conta-azul.types';

export class ContaAzulRepository {
  // === OAuth ===

  async authorize(customerId: string): Promise<AuthorizeResponse> {
    const response = await apiClientV2.get<AuthorizeResponse>(
      `/conta-azul/authorize?customerId=${customerId}`
    );
    return response.data;
  }

  async callback(code: string, state: string): Promise<CallbackResponse> {
    const response = await apiClientV2.get<CallbackResponse>(
      `/conta-azul/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`
    );
    return response.data;
  }

  // === Activation (dev auto-auth) ===

  async activate(customerId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClientV2.post<{ success: boolean; message: string }>(
      '/conta-azul/activate',
      { customerId }
    );
    return response.data;
  }

  // === Integration Status ===

  async getIntegration(customerId: string): Promise<ContaAzulIntegration | null> {
    const response = await apiClientV2.get<ContaAzulIntegration | null>(
      `/conta-azul/integration?customerId=${customerId}`
    );
    return response.data;
  }

  async refreshToken(customerId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClientV2.post<{ success: boolean; message: string }>(
      '/conta-azul/refresh-token',
      { customerId }
    );
    return response.data;
  }

  async deactivate(integrationId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClientV2.post<{ success: boolean; message: string }>(
      `/conta-azul/deactivate/${integrationId}`
    );
    return response.data;
  }

  // === Master Data ===

  async getCostCenters(customerId: string): Promise<CostCenter[]> {
    const response = await apiClientV2.get<{ data: CostCenter[] }>(
      `/conta-azul/cost-centers?customerId=${customerId}`
    );
    return response.data.data;
  }

  async getFinancialAccounts(customerId: string): Promise<FinancialAccount[]> {
    const response = await apiClientV2.get<{ data: FinancialAccount[] }>(
      `/conta-azul/financial-accounts?customerId=${customerId}`
    );
    return response.data.data;
  }

  async getCategories(customerId: string): Promise<Category[]> {
    const response = await apiClientV2.get<{ data: Category[] }>(
      `/conta-azul/categories?customerId=${customerId}`
    );
    return response.data.data;
  }

  async getExpenseCategories(customerId: string): Promise<Category[]> {
    const response = await apiClientV2.get<{ data: Category[] }>(
      `/conta-azul/expense-categories?customerId=${customerId}`
    );
    return response.data.data;
  }

  async getSuppliers(customerId: string): Promise<Supplier[]> {
    const response = await apiClientV2.get<{ data: Supplier[] }>(
      `/conta-azul/suppliers?customerId=${customerId}`
    );
    return response.data.data;
  }

  async createSupplier(input: CreateSupplierInput): Promise<Supplier> {
    const response = await apiClientV2.post<{ data: Supplier }>(
      '/conta-azul/suppliers',
      input
    );
    return response.data.data;
  }

  // === Entry Data ===

  async getEntryData(entryId: string): Promise<ContaAzulEntryData> {
    const response = await apiClientV2.get<{ data: ContaAzulEntryData }>(
      `/conta-azul/entries/${entryId}/data`
    );
    return response.data.data;
  }

  async upsertEntryData(
    entryId: string,
    data: ContaAzulEntryDataInput
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClientV2.put<{ success: boolean; message: string }>(
      `/conta-azul/entries/${entryId}/data`,
      data
    );
    return response.data;
  }

  // === Sync Entries ===

  async syncEntry(entryId: string, customerId: string): Promise<SyncJobResponse> {
    const response = await apiClientV2.post<SyncJobResponse>(
      `/conta-azul/sync-entry/${entryId}`,
      { customerId }
    );
    return response.data;
  }

  // === Sync Receipts (retry) ===

  async syncReceipt(receiptId: string): Promise<SyncJobResponse> {
    const response = await apiClientV2.post<SyncJobResponse>(
      `/conta-azul/sync-receipt/${receiptId}`,
      {},
    );
    return response.data;
  }

}
